import { Button, IconButton } from '@chakra-ui/button';
import { useColorMode, useColorModeValue } from '@chakra-ui/color-mode';
import { Icon } from '@chakra-ui/icon';
import { Flex, Box, Text, Heading, Stack } from '@chakra-ui/layout';
import { Skeleton } from '@chakra-ui/skeleton';
import { isDragActive, motion } from 'framer-motion';
import { useState } from 'react';
import { useBoolean } from '@chakra-ui/hooks';
import { CircularProgress } from '@chakra-ui/progress';
import {
    AlarmIndicator,
    ChevronUpIcon,
    DotsIcons,
    ExternalLinkIcon,
    ChevronDownIcon,
    MinusCircleIcon,
    EyeIcon,
    EyeOffIcon
} from './UiElements';
import { Tooltip } from '@chakra-ui/tooltip';
import usePersistStore from '../stores/persistStore';
import { tooltipAnimation, tooltipDownAnimation } from './customTheme';
import React from 'react';
import FavIcon from './FavIcon';
import ItemTab from './ItemTab';
import {
    DndContext,
    DragOverlay,
    MouseSensor,
    closestCenter,
    pointerWithin,
    rectIntersection,
    useSensors,
    useSensor
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    useSortable,
    verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import useDndStore from '../stores/dndStore';
import { Center, PopoverContent, Portal } from '@chakra-ui/react';
import useStore from '../stores/store';
import { findGroup, findTab } from '../Utils/findDataFunctions';
import { createPortal } from 'react-dom';
import {
    openNewGroupFromFolder,
    openSingleTab,
    updateSingleTab
} from '../../platformFiles/browserAPI';
import useSelectStore from '../stores/selectStore';
import { useItemSize } from '../Utils/useItemSize';
import EditableTitle from './EditableTitle';
import EmojiPicker from './EmojiPicker';
import { openLink } from '../Utils/tabUtils';

interface ItemProps {
    data: {
        id: string;
        title: string;
        dateCreated?: string;
        isStacked?: boolean;
        stackedItems?: any[];
        emoji?: string;
    };
    groupId?: string;
    insideActiveGroup?: string;
    hoverList?: boolean;
    setLoadingState?: any;
    loading?: boolean;
    isDragging?: boolean;
    overlayItem?: boolean;
}

export const ItemStack: React.FC<ItemProps> = React.memo(
    ({
        groupId,
        data,
        hoverList,
        setLoadingState,
        loading,
        isDragging,
        overlayItem
    }) => {
        const { title, dateCreated, id, stackedItems } = data;
        const [activeId, setActiveId] = useState(null);
        const [dragOutside, setDragOutside] = useState(false);

        const iconDarkerColor = useColorModeValue('gray.600', '#A1A2A4');
        const bgColor = useColorModeValue('#FFF', '#1e2123');
        const iconBG = useColorModeValue('transparent', '#32363A');

        let activationConstraint = {
            distance: 2
        };
        const dragAndDropSensors = useSensors(
            useSensor(MouseSensor, { activationConstraint })
        );

        const persistStore = usePersistStore();
        const [showList, setShowList] = useState(() => persistStore.getStackShowList(id));
        const [hideContent, setHideContent] = useState(() => persistStore.getStackHideContent(id));

        const toggleShowList = (e) => {
            e.stopPropagation();
            useSelectStore.getState().clearSelection();
            const newShowList = !showList;
            setShowList(newShowList);
            persistStore.setStackShowList(id, newShowList);
            setHideContent(false);
            persistStore.setStackHideContent(id, false);
        };

        const toggleHideContent = (e) => {
            e.stopPropagation();
            const newHideContent = !hideContent;
            setHideContent(newHideContent);
            persistStore.setStackHideContent(id, newHideContent);
        };

        const submitNewTitle = (newTitle) => {
            let tabGroups = structuredClone(useStore.getState().tabGroups);
            let group = findGroup(tabGroups, groupId);
            let item = findTab(group, id);
            item.title = newTitle;
            useStore.getState().updateTabGroups(tabGroups);
        };

        /* const submitNewEmoji = (newEmoji) => {
            let tabGroups = structuredClone(useStore.getState().tabGroups);
            let group = findGroup(tabGroups, groupId);
            let item = findTab(group, id);
            item.emoji = newEmoji;
            useStore.getState().updateTabGroups(tabGroups);
        }; */

        const openAllthisWindow = (e) => {
            e.stopPropagation();
            var itemsProcessed = 0;
            var idArray = [];
            let group = {
                emoji: data.emoji ? data.emoji : '📁',
                title: title ? ' ' + title : '',
                tabs: [...stackedItems]
            };
            openNewGroupFromFolder(group);
        };

        const [loadingAllSites, setLoadingAllSites] = useState(false);

        // const statusUpdate = (tabId, changeInfo, tab) => {
        //     if (changeInfo.status === 'loading') {
        //         setLoadingAllSites(true);
        //     }
        //     if (changeInfo.status === 'complete') {
        //         setLoadingAllSites(false);
        //         chrome.tabs.onUpdated.removeListener(statusUpdate);
        //         return;
        //     }
        // };

        const handleDragStart = (active) => {
            setActiveId(active.id);
            useDndStore.getState().setGlobalIsDragging(true);
        };
        const handleDragEnd = (active, over) => {
            let tabGroups = structuredClone(useStore.getState().tabGroups);
            let overContainer = findGroup(tabGroups, groupId);
            let tabIndex = overContainer.tabs.findIndex(
                (tab) => tab.id === data.id
            );
            if (dragOutside) {
                if (overContainer.tabs[tabIndex].stackedItems.length === 2) {
                    overContainer.tabs.splice(
                        tabIndex,
                        1,
                        ...overContainer.tabs[tabIndex].stackedItems
                    );
                } else {
                    let overTabIndex = overContainer.tabs[
                        tabIndex
                    ].stackedItems.findIndex((tab) => tab.id === active.id);
                    let [removedTab] = overContainer.tabs[
                        tabIndex
                    ].stackedItems.splice(overTabIndex, 1);
                    if (tabIndex + 1 >= overContainer.tabs.length) {
                        overContainer.tabs.push(removedTab);
                    } else {
                        overContainer.tabs.splice(tabIndex + 1, 0, removedTab);
                    }
                }
            } else {
                if (active.id === over.id) {
                    setDragOutside(false);
                    setTimeout(() => {
                        setActiveId(null);
                    }, 200);
                    return;
                }
                const oldIndex = overContainer.tabs[
                    tabIndex
                ].stackedItems.findIndex((tab) => tab.id === active.id);
                const newIndex = overContainer.tabs[
                    tabIndex
                ].stackedItems.findIndex((tab) => tab.id === over.id);

                overContainer.tabs[tabIndex].stackedItems = arrayMove(
                    overContainer.tabs[tabIndex].stackedItems,
                    oldIndex,
                    newIndex
                );
            }
            useStore
                .getState()
                .updateTabGroups(tabGroups, { type: 'items', ids: [] }); // TODO fixa id
            setDragOutside(false);
            setActiveId(null);
            // setTimeout(() => {
            //     setActiveId(null);
            // }, 200);
        };
        const handleDragCancel = () => {
            setDragOutside(false);
            setActiveId(null);
            // setTimeout(() => {
            //     setActiveId(null);
            // }, 200);
        };

        const TopRowButtons = () => (
            <Flex
                pointerEvents="auto"
                zIndex="3"
                position="absolute"
                right="-12px"
                top="8px"
                cursor={overlayItem ? 'grabbing' : 'grab'}
            >
                <Box
                    ml="4px"
                    mr="2px"
                    opacity={hoverList && !isDragging ? '1' : '0'}
                    transition="0.3s cubic-bezier(0.175, 0.885, 0.320, 1.275)"
                    transform={
                        hoverList && !isDragging ? 'scale(1)' : 'scale(0)'
                    }
                >
                    <Tooltip
                        label={hideContent ? 'Show content' : 'Hide content'}
                        //showDelay="300"
                        zIndex="500000"
                        rounded="6px"
                        motionProps={tooltipDownAnimation}
                    >
                        <IconButton
                            boxSize="24px"
                            icon={hideContent ? <EyeIcon /> : <EyeOffIcon />}
                            onClick={(e) => {
                                toggleHideContent(e);
                            }}
                            _active={{
                                transform: 'scale(0.84)'
                            }}
                            _focus={{
                                boxShadow: ''
                            }}
                            isLoading={loadingAllSites}
                            outline="none"
                            color={iconDarkerColor}
                            minWidth="28px"
                            height="28px"
                            bg={iconBG}
                            aria-label={''} //returnFocusOnClose={false}
                        />
                    </Tooltip>
                </Box>
                <Box
                    ml="4px"
                    mr="2px"
                    opacity={hoverList && !isDragging ? '1' : '0'}
                    transition="0.3s cubic-bezier(0.175, 0.885, 0.320, 1.275)"
                    transform={
                        hoverList && !isDragging ? 'scale(1)' : 'scale(0)'
                    }
                >
                    <Tooltip
                        label={'Open as group'}
                        //showDelay="300"
                        zIndex="500000"
                        rounded="6px"
                        motionProps={tooltipDownAnimation}
                    >
                        <IconButton
                            boxSize="24px"
                            icon={<ExternalLinkIcon />}
                            onClick={(e) => {
                                openAllthisWindow(e);
                            }}
                            _active={{
                                transform: 'scale(0.84)'
                            }}
                            _focus={{
                                boxShadow: ''
                            }}
                            isLoading={loadingAllSites}
                            outline="none"
                            color={iconDarkerColor}
                            minWidth="28px"
                            height="28px"
                            bg={iconBG}
                            aria-label={''} //returnFocusOnClose={false}
                        />
                    </Tooltip>
                </Box>
                <Box
                    ml="4px"
                    mr="12px"
                    opacity={hoverList && !isDragging ? '1' : '0'}
                    transition="0.3s cubic-bezier(0.175, 0.885, 0.320, 1.275)"
                    transform={
                        hoverList && !isDragging ? 'scale(1)' : 'scale(0)'
                    }
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    <Tooltip
                        label={showList ? 'Collapse' : 'Expand'}
                        //showDelay="300"
                        zIndex="500000"
                        rounded="6px"
                        motionProps={tooltipDownAnimation}
                    >
                        <IconButton
                            boxSize="24px"
                            icon={
                                showList ? (
                                    <ChevronUpIcon />
                                ) : (
                                    <ChevronDownIcon />
                                )
                            }
                            onClick={(e) => {
                                toggleShowList(e);
                            }}
                            _active={{
                                transform: 'scale(0.84)'
                            }}
                            _focus={{
                                boxShadow: ''
                            }}
                            outline="none"
                            color={iconDarkerColor}
                            minWidth="28px"
                            height="28px"
                            bg={iconBG}
                            aria-label={''} //returnFocusOnClose={false}
                        />
                    </Tooltip>
                </Box>
            </Flex>
        );

        return (
            <Box w="100%">
                <Flex alignItems={'center'} pos="relative" mb={hideContent ? "8px" :"12px"} pt="8px">
                <Tooltip
                        label="Edit symbol"
                        zIndex="500000"
                        placement="top"
                        rounded="6px"
                        motionProps={tooltipAnimation}
                    >
                        <div onClick={(e) => e.stopPropagation()}>
                            <EmojiPicker
                                emoji={data.emoji ? data.emoji : '📁'}
                                type={'stack'}
                                id={id}
                                groupId={groupId}
                            />
                        </div>
                    </Tooltip>
                    <Tooltip
                        label="Edit title"
                        zIndex="500000"
                        placement="top"
                        rounded="6px"
                        motionProps={tooltipAnimation}
                    >
                        <div>
                            <EditableTitle
                                ml={'4px'}
                                title={
                                    title
                                        ? title
                                        : stackedItems.length + ' SITES'
                                }
                                size={'sm'}
                                onSubmit={submitNewTitle}
                                color={'gray.400'}
                            />
                        </div>
                    </Tooltip>
                    {!overlayItem && <TopRowButtons />}
                </Flex>
                {hideContent ? null :
                    !showList ? (
                    <Flex
                        w="100%"
                        flexWrap="wrap"
                        alignItems="center"
                        justifyContent="flex-start"
                        mt="8px"
                        mb="8px"
                        gap="16px"
                    >
                        {stackedItems.map((site, index) => (
                            <SingleIconItem key={site.id} site={site} />
                        ))}
                    </Flex>
                ) : (
                    <DndContext
                        sensors={dragAndDropSensors}
                        collisionDetection={(args) =>
                            stackedCollisionDetectionAlgorithm({
                                ...args,
                                dragOutside,
                                setDragOutside
                            })
                        }
                        onDragStart={({ active }) => handleDragStart(active)}
                        onDragEnd={({ active, over }) =>
                            handleDragEnd(active, over)
                        }
                        onDragCancel={() => handleDragCancel()}
                    >
                        <SortableContext
                            items={stackedItems}
                            strategy={verticalListSortingStrategy}
                        >
                            <Stack
                                pb="8px"
                                mt="-4px"
                                onMouseDown={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                }}
                                onPointerDownCapture={(e) => {
                                    e.stopPropagation();
                                }}
                            >
                                {stackedItems.map((site, index) => (
                                    <SiteInList
                                        activeId={activeId}
                                        key={site.id}
                                        site={site}
                                        groupId={groupId}
                                    />
                                ))}
                            </Stack>
                        </SortableContext>
                        <Portal>
                            <DragOverlay>
                                <Flex
                                    // onMouseEnter={hover.on}
                                    // onMouseOver={hover.on}
                                    // onMouseLeave={hover.off}
                                    // bg={'red'}
                                    alignItems="center"
                                    bg={bgColor}
                                    rounded="10px"
                                    p="4px"
                                    cursor="grabbing"
                                    boxShadow={'inset 0px 0px 0px 2px #44E18C'}
                                >
                                    <ItemTab
                                        data={stackedItems.find(
                                            (site) => site.id === activeId
                                        )}
                                        isActiveTab={false}
                                        groupId={groupId}
                                        hoverSite={false}
                                        setLoadingState={false}
                                        loading={false}
                                        overlayItem={true}
                                        hideTooltip={true}
                                    />
                                </Flex>
                            </DragOverlay>
                        </Portal>
                    </DndContext>
                )}
                {dragOutside && (
                    <Portal>
                        <Box
                            position="fixed"
                            top="0"
                            left="0"
                            width="full"
                            height="full"
                            backgroundColor="rgba(0, 0, 0, 0.5)"
                            backdropFilter="blur(2px)"
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            // zIndex="overlay"
                        >
                            <Center
                                display={'flex'}
                                p="16px 24px"
                                rounded="8px"
                                bg="te1"
                                border="var(--chakra-colors-teBorder)"
                                zIndex="modal"
                                boxShadow={'0 16px 24px -6px rgba(0,0,0,0.80)'}
                            >
                                <Box mr="8px" my="auto">
                                    <MinusCircleIcon />
                                </Box>
                                <Text fontSize={'16px'} fontWeight={500}>
                                    Drop to remove 1 item from stack
                                </Text>
                            </Center>
                        </Box>
                    </Portal>
                )}
            </Box>
        );
    }
);

export default ItemStack;

const SingleIconItem = ({ site }) => {
    const url = site.url;
    const [isLoading, setIsLoading] = useState(false);

    const outerShadow = useColorModeValue(
        '0 1px 4px -1px rgba(78,80,81,0.4), 0 1px 2px 0px rgba(78,80,81,0.22)',
        '0 3px 10px -3px rgba(0,0,0,0), 0 0px 0px 1px rgb(0 0 0 / 0%)'
    );
    const hoverShadow = useColorModeValue(
        '0 4px 8px 0px rgb(135 145 155 / 36%), 0 0px 0px 1px rgba(78,80,81,0.1)',
        '0 3px 10px -3px rgba(0,0,0,0.90), 0 0px 0px 1px rgb(0 0 0 / 20%)'
    );

    const { favIconOuterSize } = useItemSize();

    return (
        <Tooltip
            placement="bottom"
            motionProps={tooltipDownAnimation}
            //onOpen={() => getThumbnailUri()}
            zIndex="1000"
            rounded="6px"
            px="8px"
            py="4px"
            openDelay={160}
            label={
                <>
                    <Box>
                        <Text
                            fontSize="13"
                            fontWeight="600"
                            //letterSpacing="0.54px"
                        >
                            {site.title}
                        </Text>
                        <Text
                            fontSize="12px"
                            opacity="0.725"
                            mt="2px"
                            wordBreak="break-all"
                            //isTruncated
                        >
                            {url}
                        </Text>
                        {site.dateCreated && (
                            <Text
                                fontSize="11px"
                                opacity="0.6"
                                mt="2px"
                                wordBreak="break-all"
                                //isTruncated
                            >
                                Saved {site.dateCreated.substring(15, 21)},{' '}
                                {site.dateCreated.substring(0, 15)}
                            </Text>
                        )}
                    </Box>
                </>
            }
        >
            <Flex
                onClick={(e: any) => openLink(e, url, setIsLoading)}
                boxShadow={outerShadow}
                _hover={{
                    boxShadow: hoverShadow,
                    transform: 'scale(1.075)'
                }}
                _active={{
                    boxShadow: outerShadow,
                    transform: 'scale(0.975)'
                }}
                transition={
                    isLoading
                        ? '0.4s cubic-bezier(0.2,-0.3,0.72,1.47)'
                        : '0.16s cubic-bezier(0.22,0.61,0.36,1)'
                }
                rounded="8px"
                width={favIconOuterSize}
                height={favIconOuterSize}
                alignItems={'center'}
                alignContent={'center'}
                cursor={'pointer'}
            >
                <FavIcon
                    //size="32px"
                    url={site.url}
                    favIcon={site.favIcon}
                    loading={false}
                    hoverSite={false} //TODO
                    isActiveTab={false}
                    title={site.title}
                    noShadow={true}
                    my="auto"
                />
            </Flex>
        </Tooltip>
    );
};

const SiteInList = ({ site, groupId, activeId }) => {
    const [hoverItem, hover] = useBoolean();
    const [isLoading, setIsLoading] = useState(false);
    const {
        attributes,
        listeners,
        setNodeRef,
        isDragging,
        transform,
        transition
    } = useSortable({
        id: site.id,
        data: {
            type: 'tab',
            groupId: groupId,
            data: site
        }
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition
    };

    return (
        <div onMouseDown={(e) => e.stopPropagation()}>
            <div
                ref={setNodeRef}
                style={{ ...style, outline: 'none' }}
                {...attributes}
                {...listeners}
            >
                <Flex
                    onMouseEnter={hover.on}
                    onMouseOver={hover.on}
                    onMouseLeave={hover.off}
                    alignItems={'center'}
                    opacity={isDragging ? '0.5' : '1'}
                >
                    <ItemTab
                        data={site}
                        isActiveTab={false}
                        groupId={groupId}
                        hoverSite={hoverItem}
                        setLoadingState={setIsLoading}
                        loading={isLoading}
                    />
                </Flex>
            </div>
        </div>
    );
};

const stackedCollisionDetectionAlgorithm = (args) => {
    const intersection: any = closestCenter({
        ...args
    });
    if (intersection.length === 0) return [];

    const closestRect =
        intersection[0]?.data?.droppableContainer?.rect?.current;

    const pointerLocation = args.pointerCoordinates;

    const distancesToEdges = {
        left: closestRect.left - pointerLocation.x,
        right: pointerLocation.x - closestRect.right,
        top: closestRect.top - pointerLocation.y,
        bottom: pointerLocation.y - closestRect.bottom
    };
    const closestEdgeDistance = Math.max(
        distancesToEdges.left,
        distancesToEdges.right,
        distancesToEdges.top,
        distancesToEdges.bottom
    );

    if (closestEdgeDistance > 50) {
        if (!args.dragOutside) {
            args.setDragOutside(true);
        }
    } else {
        if (args.dragOutside) {
            args.setDragOutside(false);
        }
    }

    return intersection;
};
